package com.tixforgigs.common.http


import com.tixforgigs.apax.api.*
import com.tixforgigs.apax.models.*
import com.tixforgigs.apax.models.Headers
import com.tixforgigs.common.CommonBuildKonfig
import com.tixforgigs.common.core.enums.ClientType
import com.tixforgigs.common.core.enums.View
import com.tixforgigs.common.core.extensions.BuildKonfigUtil.calculateApiUrl
import com.tixforgigs.common.core.extensions.BuildKonfigUtil.calculateAuthUrl
import com.tixforgigs.common.core.extensions.BuildKonfigUtil.calculateInternalApiUrl
import com.tixforgigs.common.core.extensions.toThrowable
import com.tixforgigs.common.core.extensions.tryCatchSentry
import com.tixforgigs.common.core.util.AuthUtil.currentBearerToken
import com.tixforgigs.common.core.util.AuthUtil.deviceId
import com.tixforgigs.common.core.util.AuthUtil.toBearerTokensOrNull
import com.tixforgigs.common.core.util.AuthUtil.uniqueId
import com.tixforgigs.common.core.util.LocalizationUtil.translationLocaleAsString
import com.tixforgigs.common.core.util.SentryUtil.captureException
import com.tixforgigs.common.core.util.StorageUtil
import com.tixforgigs.common.core.util.StorageUtil.setLocalStorageItem
import com.tixforgigs.common.core.util.ToastUtil.showThrowableToast
import io.github.oshai.kotlinlogging.KotlinLogging
import io.github.oshai.kotlinlogging.logger
import io.ktor.client.*
import io.ktor.client.HttpClient
import io.ktor.client.call.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.auth.*
import io.ktor.client.plugins.auth.providers.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.cookies.*
import io.ktor.client.plugins.logging.*
import io.ktor.client.request.*
import io.ktor.client.request.forms.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.browser.window
import kotlinx.coroutines.flow.updateAndGet
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.json.Json


expect fun platformHttpClient(config: HttpClientConfig<*>.() -> Unit = {}): HttpClient

object HttpClient {

  lateinit var CLIENT_TYPE: ClientType
    private set
  lateinit var FLAVOR: Flavor
    private set
  lateinit var BASE_URL: String
    private set
  lateinit var AUTH_CLIENT_ID: String
    private set
  lateinit var API_CLIENT_ID: String
    private set
  lateinit var API_URL: String
    private set
  lateinit var AUTH_URL: String
    private set
  lateinit var INTERNAL_API_URL: String
    private set
  lateinit var SENTRY_URL: String
    private set

  lateinit var eventsApi: CommonEventsApi
    private set
  lateinit var cartsApi: CommonCartApi
    private set
  lateinit var ordersApi: CommonOrdersApi
    private set
  lateinit var invoiceApi: CommonOrdersApi
    private set
  lateinit var accountApi: CommonAccountApi
    private set
  lateinit var ticketsApi: CommonTicketsApi
    private set
  lateinit var templateApi: CommonTemplateApi
    private set
  lateinit var authApi: CommonAuthApi
    private set
  lateinit var trackerApi: CommonTrackerApi
    private set
  lateinit var systemApi: CommonSystemApi
    private set

  private var initialized = false

  val log by KotlinLogging.logger()
  val cartScope = Mutex()

  fun init(
    type: ClientType,
    flavor: Flavor,
    baseUrl: String,
    authClientId: String,
    apiClientId: String,
    sentryUrl: String,
  ) {
    CLIENT_TYPE = type
    FLAVOR = flavor
    BASE_URL = baseUrl
    AUTH_CLIENT_ID = authClientId
    API_CLIENT_ID = apiClientId
    API_URL = calculateApiUrl(flavor)
    AUTH_URL = calculateAuthUrl(flavor)
    INTERNAL_API_URL = calculateInternalApiUrl(flavor)
    SENTRY_URL = sentryUrl

    eventsApi = CommonEventsApi(API_URL, httpClientConfig = { config ->
      config.apiConfig()
    })
    cartsApi = CommonCartApi(API_URL, httpClientConfig = { config ->
      config.apiConfig()
    })
    ordersApi = CommonOrdersApi(API_URL, httpClientConfig = { config ->
      config.apiConfig()
    })
    invoiceApi = CommonOrdersApi(API_URL, httpClientConfig = { config ->
      config.pdfConfig()
    })
    accountApi = CommonAccountApi(API_URL, httpClientConfig = { config ->
      config.apiConfig()
    })
    ticketsApi = CommonTicketsApi(API_URL, httpClientConfig = { config ->
      config.apiConfig()
    })
    templateApi = CommonTemplateApi(API_URL, httpClientConfig = { config ->
      config.apiConfig()
    })
    authApi = CommonAuthApi(AUTH_URL, httpClientConfig = { config ->
      config.authConfig()
    })
    trackerApi = CommonTrackerApi("https://ping.company.tixforgigs.com", httpClientConfig = { config ->
      config.apiConfig()
    })
    systemApi = CommonSystemApi(INTERNAL_API_URL, httpClientConfig = { config ->
      config.apiConfig()
    })

    initialized = true
  }

  fun ensureInitialized() {
    check(initialized) { "HttpClient not initialized. Call HttpClient.init(...) first." }
  }

  suspend inline fun <reified T> apiV4Get(
    type: ApiGetPath,
    onStart: () -> Unit = {},
    onCompletionWithError: (Throwable, ApiError?, HttpResponse?) -> Unit = { _, _, _ -> },
    onCompletionSuccess: () -> Unit = {},
  ): T? {
    onStart()
    return runCatching {
      when (type) {
        is ApiGetPath.EventsByTemplateId -> {
          eventsApi.eventsTemplate(
            templateId = type.templateId,
            details = type.details,
            page = type.page,
            size = type.size,
            sort = type.sort
          )
        }

        is ApiGetPath.Events -> {
          eventsApi.eventsAll()
        }

        is ApiGetPath.EventById -> {
          eventsApi.eventDetails(type.eventId)
        }

        is ApiGetPath.Orders -> {
          ordersApi.ordersAll(
            dbVersion = type.dbVersion,
            details = type.details,
            page = type.page,
            size = type.size,
            sort = type.sort,
          )
        }

        is ApiGetPath.OrderById -> {
          ordersApi.orderDetails(type.orderId, type.orderToken)
        }

        is ApiGetPath.Tickets -> {
          ticketsApi.ticketAll(
            eventId = type.eventId,
            contingentId = type.contingentId,
            venueId = type.venueId,
            promoterId = type.promoterId,
            dbVersion = type.dbVersion,
            page = type.page,
            size = type.size,
            sort = type.sort,
          )
        }

        is ApiGetPath.Invoice -> {
          ordersApi.orderInvoice(type.orderId, type.invoiceId, type.orderToken)
        }

        is ApiGetPath.Profile -> {
          accountApi.accountProfile()
        }

        is ApiGetPath.TicketResaleConfig -> {
          ticketsApi.ticketResaleConfig(type.ticketId)
        }

        is ApiGetPath.TemplateConfigs -> {
          templateApi.templateListGet()
        }

        is ApiGetPath.TemplateConfigByTemplateId -> {
          templateApi.templateConfigGet(type.templateId)
        }

        is ApiGetPath.TemplateFilterSearch -> {
          templateApi.templateSearch(type.searchText, type.searchType)
        }

        is ApiGetPath.TemplateStyle -> {
          templateApi.templateStyle(type.templateId)
        }

        is ApiGetPath.SystemCountries -> {
          systemApi.systemCountries()
        }
      }
    }.getOrElse { error ->
      showThrowableToast(throwable = error)
      onCompletionWithError(error, null, null)
      captureException(error)
      return null
    }.onComplete<T>(
      onCompletionSuccess = onCompletionSuccess,
      onCompletionWithError = onCompletionWithError,
    )
  }

  suspend inline fun <reified T> apiV4Post(
    type: ApiPostPath,
    onStart: () -> Unit = {},
    onCompletionWithError: (Throwable, ApiError?, HttpResponse?) -> Unit = { _, _, _ -> },
    onCompletionSuccess: () -> Unit = {},
  ): T? {
    onStart()
    return runCatching {
      when (type) {
        is ApiPostPath.CartCreate -> {
          cartsApi.cartDetails(type.body)
        }

        is ApiPostPath.CartAdd -> {
          cartScope.withLock {
            cartsApi.cartItemAdd(type.body)
          }
        }

        is ApiPostPath.CartRemove -> {
          cartScope.withLock {
            cartsApi.cartItemRemove(type.body)
          }
        }

        is ApiPostPath.CartRefresh -> {
          cartScope.withLock {
            cartsApi.cartRefresh(type.body)
          }
        }

        // todo - how to react on status 204 (no content)
        is ApiPostPath.CartOptions -> {
          cartScope.withLock {
            cartsApi.cartOptions(type.body)
          }
        }

        is ApiPostPath.CartFloorplanBestAvailable -> {
          cartScope.withLock {
            cartsApi.cartFloorplanBestavailable(type.body)
          }
        }

        is ApiPostPath.CartAddVoucher -> {
          cartScope.withLock {
            cartsApi.cartVoucherApply(type.body)
          }
        }

        is ApiPostPath.CartRemoveVoucher -> {
          cartScope.withLock {
            cartsApi.cartVoucherRemove(type.body)
          }
        }

        is ApiPostPath.OrderCreate -> {
          ordersApi.orderSubmit(type.body)
        }

        is ApiPostPath.OrderRestart -> {
          ordersApi.orderRestart(type.orderId, type.orderToken, type.body)
        }

        is ApiPostPath.OrderApprove -> {
          ordersApi.orderApprove(type.orderId, type.orderToken, type.body)
        }

        is ApiPostPath.AccountLogin -> {
          accountApi.accountLogin(type.body)
        }

        is ApiPostPath.Track -> {
          trackerApi.trackerPing(type.body)
        }

        is ApiPostPath.TemplateConfigCreate -> {
          templateApi.templateCreate(type.body)
        }

        is ApiPostPath.TemplateConfigUpdate -> {
          templateApi.templateConfigUpdate(type.templateId, type.body)
        }

        is ApiPostPath.AuthToken -> {
          authApi.authToken(type.body)
        }

        is ApiPostPath.AccountAddressCreate -> {
          accountApi.accountAddressAdd(type.body)
        }

        is ApiPostPath.AccountBankDetailsCreate -> {
          accountApi.accountBankDetailsAdd(type.body)
        }

        is ApiPostPath.TicketResaleCreate -> {
          ticketsApi.ticketResaleCreate(type.body)
        }

        is ApiPostPath.AccountPasswordReset -> {
          accountApi.accountPasswordReset(type.body)
        }

        is ApiPostPath.AccountPasswordUpdate -> {
          accountApi.accountPasswordUpdate(type.body)
        }

        is ApiPostPath.AccountUserCreate -> {
          accountApi.accountUserAdd(type.body)
        }
      }
    }.getOrElse { error ->
      showThrowableToast(throwable = error)
      onCompletionWithError(error, null, null)
      captureException(error)
      return null
    }.onComplete<T>(
      onCompletionSuccess = onCompletionSuccess,
      onCompletionWithError = onCompletionWithError,
    )
  }

  suspend inline fun <reified T> apiV4Update(
    type: ApiUpdatePath,
    onStart: () -> Unit = {},
    onCompletionWithError: (Throwable, ApiError?, HttpResponse?) -> Unit = { _, _, _ -> },
    onCompletionSuccess: () -> Unit = {},
  ): T? {
    onStart()
    return runCatching {
      when (type) {
        is ApiUpdatePath.CartItemUpdate -> {
          cartScope.withLock {
            cartsApi.cartItemUpdate(type.body)
          }
        }

        is ApiUpdatePath.AccountUserUpdate -> {
          accountApi.accountUserUpdate(type.body)
        }

        is ApiUpdatePath.AccountAddressUpdate -> {
          accountApi.accountAddressUpdate(type.body)
        }

        is ApiUpdatePath.AccountBankDetailsUpdate -> {
          accountApi.accountBankDetailsUpdate(type.body)
        }

        is ApiUpdatePath.AccountCompanyUpdate -> TODO()
      }
    }.getOrElse { error ->
      showThrowableToast(throwable = error)
      onCompletionWithError(error, null, null)
      captureException(error)
      return null
    }.onComplete<T>(
      onCompletionSuccess = onCompletionSuccess,
      onCompletionWithError = onCompletionWithError,
    )
  }

  suspend inline fun <reified T> apiV4Delete(
    type: ApiDeletePath,
    onStart: () -> Unit = {},
    onCompletionWithError: (Throwable, ApiError?, HttpResponse?) -> Unit = { _, _, _ -> },
    onCompletionSuccess: () -> Unit = {},
  ): T? {
    onStart()
    return runCatching {
      when (type) {
        is ApiDeletePath.AccountAddressDelete -> {
          accountApi.accountAddressDelete(type.addressId)
        }

        is ApiDeletePath.AccountBankDelete -> {
          accountApi.accountBankDetailsDelete(type.bankAccountId)
        }

        is ApiDeletePath.TicketResaleDelete -> {
          ticketsApi.ticketResaleCancel(type.resaleId)
        }

        is ApiDeletePath.AccountUserDelete -> TODO()

        is ApiDeletePath.TemplateConfigDelete -> {
          templateApi.templateDelete(type.templateId)
        }
      }
    }.getOrElse { error ->
      showThrowableToast(throwable = error)
      onCompletionWithError(error, null, null)
      captureException(error)
      return null
    }.onComplete<T>(
      onCompletionSuccess = onCompletionSuccess,
      onCompletionWithError = onCompletionWithError,
    )
  }

  suspend inline fun <reified T> com.tixforgigs.apax.infrastructure.HttpResponse<out Any>.onComplete(
    onCompletionWithError: (Throwable, ApiError?, HttpResponse?) -> Unit = { _, _, _ -> },
    onCompletionSuccess: () -> Unit = {},
  ): T? {
    return when (status) {
      HttpStatusCode.OK.value -> {
        runCatching { body() as T }
          .onFailure { error ->
            if (FLAVOR != Flavor.PROD) {
              log.debug { "STATUS: $status // ERROR: $error" }
              showThrowableToast(throwable = error, httpResponse = response)
            }
            onCompletionWithError(error, null, null)
            captureException(error)
          }
          .onSuccess { onCompletionSuccess() }
          .getOrNull()
      }

      in HttpStatusCode.Created.value until HttpStatusCode.PartialContent.value -> {
        runCatching { body() as T? }
          .onFailure { error ->
            if (FLAVOR != Flavor.PROD) {
              log.debug { "STATUS: $status // ERROR: $error" }
              showThrowableToast(throwable = error, httpResponse = response)
            }
            onCompletionWithError(error, null, null)
            captureException(error)
          }
          .onSuccess { onCompletionSuccess() }
          .getOrNull()
      }

      in HttpStatusCode.BadRequest.value until HttpStatusCode.BadGateway.value -> {
        runCatching {
          response.body<ApiError>()
        }.onFailure { error ->
          if (FLAVOR != Flavor.PROD) {
            log.debug { "STATUS: $status // ApiError Serialization Error: $error" }
            showThrowableToast(throwable = error, httpResponse = response)
          }
          captureException(error)
        }.getOrNull()?.let { apiError ->
          if (FLAVOR != Flavor.PROD) {
            log.debug { "STATUS: $status // ERROR: ${apiError.toThrowable()}" }
            showThrowableToast(apiError = apiError, httpResponse = response)
          }
          onCompletionWithError(apiError.toThrowable(), apiError, response)
        }
        null
      }

      else -> {
        val error =
          Throwable("Http Status Code: $status Request: ${response.request.let { "${it.method}: ${it.url}" }}")
        showThrowableToast(throwable = error, httpResponse = response)
        captureException(error)
        null
      }
    }
  }

  suspend fun oauthTokenRequest(
    code: String,
    grantType: GrantType,
    clientType: ClientType,
  ): AuthToken? {
    val response = platformHttpClient { authConfig() }.post {
      if (clientType == ClientType.TEMPLATE_CONFIGURATOR && grantType == GrantType.PASSWORD) {
        url {
          takeFrom(window.location.origin)
          appendPathSegments("api", "token")
          headers {
            append(HttpHeaders.ContentType, ContentType.Application.FormUrlEncoded)
            append(HttpHeaders.Accept, ContentType.Application.Json)
          }
        }
        setBody(
          FormDataContent(
            Parameters.build {
              append("code", code)
            }
          )
        )
      } else {
        url {
          takeFrom(AUTH_URL)
          appendPathSegments("oauth", "token")
          headers {
            append(HttpHeaders.ContentType, ContentType.Application.FormUrlEncoded)
            append(HttpHeaders.Accept, ContentType.Application.Json)
          }
        }
        setBody(
          FormDataContent(
            Parameters.build {
              append("grant_type", grantType.value)
              append("code", code)
              append("tfg_device_id", deviceId())
              append("client_id", AUTH_CLIENT_ID)
              append("state", uniqueId.toString())
              append(
                "redirect_uri",
                "${BASE_URL}${View.TOKEN.path}"
              )
            }
          )
        )
      }
    }
    return tryCatchSentry {
      response.body<AuthToken>()
    }.onFailure { error ->
      showThrowableToast(throwable = error, httpResponse = response)
    }.getOrNull()
  }

  private fun HttpClientConfig<*>.apiConfig() {
    defaultPlugins()
    install(HttpCookies) {}
    install(Auth) {
      bearer {
        cacheTokens = false
        loadTokens {
          currentBearerToken.value.toBearerTokensOrNull()
        }
        refreshTokens {
          tryCatchSentry {
            apiV4Post<AuthToken>(
              type = ApiPostPath.AuthToken(
                body = GrantRequest(
                  grantType = GrantType.REFRESH_TOKEN,
                  clientId = AUTH_CLIENT_ID,
                  refreshToken = currentBearerToken.value?.refreshToken.orEmpty(),
                  tfgDeviceId = deviceId()
                )
              )
            )
          }.getOrNull()?.let { authToken ->
            currentBearerToken
              .updateAndGet { authToken }
              .also {
                setLocalStorageItem<AuthToken>(
                  StorageUtil.StorageKey.AuthToken,
                  authToken,
                  AuthToken.serializer()
                )
              }.toBearerTokensOrNull()
          }
        }
        sendWithoutRequest { request ->
          request.url.encodedPath != "/auth/oauth/token"
        }
      }
    }
    defaultRequest {
      headers.append(HttpHeaders.AcceptLanguage, translationLocaleAsString)
      headers.append(Headers.CLIENT_ID.value, API_CLIENT_ID)
      headers.append(Headers.API_VERSION.value, CommonBuildKonfig.API_VERSION)
    }
  }

  fun HttpClientConfig<*>.authConfig() {
    defaultPlugins()
    defaultRequest {
      headers.append(HttpHeaders.AcceptLanguage, translationLocaleAsString)
    }
  }

  private fun HttpClientConfig<*>.tixSupportConfig() {
    defaultPlugins()
    defaultRequest {
      headers.append(HttpHeaders.AcceptLanguage, translationLocaleAsString)
//      headers.append(Headers.CLIENT_ID.value, BuildKonfig.CLIENT_ID)
//      headers.append(Headers.API_VERSION.value, BuildKonfig.API_VERSION)
    }
  }

  private fun HttpClientConfig<*>.pdfConfig() {
    defaultPlugins()
    defaultRequest {
      accept(ContentType.Application.Pdf) //headers.append(HttpHeaders.Accept, "application/pdf")
      headers.append(HttpHeaders.ContentDisposition, "attachment; filename=\"invoice.pdf\"")
    }
  }

  private fun HttpClientConfig<*>.defaultPlugins() {
    install(Logging) {
      level = when (FLAVOR) {
        Flavor.PROD -> LogLevel.NONE
        Flavor.DEV, Flavor.STAGING -> LogLevel.INFO
        Flavor.LOCAL -> LogLevel.ALL
      }
    }
    install(ContentNegotiation) {
      json(Json {
        prettyPrint = true
        isLenient = true
        ignoreUnknownKeys = true
      })
    }
  }
}
